import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { listCategories, listTemplates } from '../data/templates';
import type { CategorySummary, TemplateSummary } from '../data/types';

interface TemplatesState {
  templates: TemplateSummary[];
  categories: CategorySummary[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useTemplates(): TemplatesState {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState<TemplateSummary[]>([]);
  const [categories, setCategories] = useState<CategorySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    () =>
      Promise.all([listTemplates(), listCategories()])
        .then(([items, cats]) => {
          setTemplates(items);
          setCategories(cats);
          setError(null);
        })
        .catch((err: unknown) => {
          setError(err instanceof Error ? err.message : t('errors.loadTemplates'));
        })
        .finally(() => {
          setLoading(false);
        }),
    [t]
  );

  const refresh = useCallback(() => {
    setLoading(true);
    return load();
  }, [load]);

  useEffect(() => {
    void load();
  }, [load]);

  return { templates, categories, loading, error, refresh };
}
